/**
 * features/analytics71a/Analytics71APanel.tsx — MER Algo Footprints v7.1B — Phase 7.1A
 */
/**
 * features/analytics71a/Analytics71APanel.tsx — Phase 7.1A (completion patch)
 *
 * Two views:
 *   Analytics — descriptive aggregations (by symbol / setup / regime / action / gamma)
 *   Log       — full journal review table with all fields, delete action
 *
 * Purely read-only with respect to engine state.
 * All groupings are deterministic aggregations over journal71aEntries.
 * No P/L math, no feedback loop to ranking/scoring.
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import { Journal71AEntry, Journal71AActionType } from '../../types';
import { ReplayPanel }               from '../71b/ReplayPanel';
import { OperatorPerformancePanel }  from '../71b/OperatorPerformancePanel';
import { exportJournalCSV, exportJournalJSON } from '../71b/export71b';
import { SessionReviewPanel }        from '../71c/SessionReviewPanel';
import { AuditExportPanel }          from '../71c/AuditExportPanel';
import { SnapshotHistoryPanel }      from '../learning/components/SnapshotHistoryPanel';
import { AdaptiveEdgeSummaryView }   from '../learning/components/AdaptiveEdgeSummaryView';
import { DisciplineScorePanel }      from '../71b/DisciplineScorePanel';

// ─── Shared style constants ───────────────────────────────────────────────────

const TH: React.CSSProperties = {
  textAlign: 'left', padding: '4px 8px', fontSize: 9, color: 'var(--t3)',
  textTransform: 'uppercase', letterSpacing: '.7px', fontFamily: 'var(--sans)',
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  padding: '4px 8px', fontSize: 10, color: 'var(--text)', fontFamily: 'var(--mono)',
  borderBottom: '1px solid var(--surface)', verticalAlign: 'top',
};

const ACTION_COLOR: Record<Journal71AActionType, string> = {
  trade_taken:      'var(--green)',
  trade_skipped:    'var(--amber)',
  setup_viewed:     'var(--blue-dim)',
  note_added:       'var(--cyan)',
  outcome_reviewed: 'var(--purple)',
};

const ACTION_LABEL: Record<Journal71AActionType, string> = {
  trade_taken:      'TAKEN',
  trade_skipped:    'SKIPPED',
  setup_viewed:     'VIEWED',
  note_added:       'NOTE',
  outcome_reviewed: 'OUTCOME',
};

const OUTCOME_COLOR = {
  win:       'var(--green)',
  loss:      'var(--red)',
  breakeven: 'var(--amber)',
};

function fmt(ts: number): string {
  const d = new Date(ts);
  return `${d.getMonth()+1}/${d.getDate()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KPI({ label, value, sub, color = 'var(--text)' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '8px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase',
        letterSpacing: '.8px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: 'var(--mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Group table (analytics views) ───────────────────────────────────────────

interface GroupRow {
  key: string;
  total: number;
  taken: number;
  skipped: number;
  viewed: number;
  notes: number;
  outcomes: number;
}

function groupBy(
  entries: Journal71AEntry[],
  keyFn: (e: Journal71AEntry) => string | null,
): GroupRow[] {
  const map = new Map<string, GroupRow>();
  for (const e of entries) {
    const k = keyFn(e) ?? '—';
    if (!map.has(k)) map.set(k, { key: k, total: 0, taken: 0, skipped: 0, viewed: 0, notes: 0, outcomes: 0 });
    const r = map.get(k)!;
    r.total++;
    if (e.actionType === 'trade_taken')      r.taken++;
    if (e.actionType === 'trade_skipped')    r.skipped++;
    if (e.actionType === 'setup_viewed')     r.viewed++;
    if (e.actionType === 'note_added')       r.notes++;
    if (e.actionType === 'outcome_reviewed') r.outcomes++;
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
}

function GroupTable({ rows, keyLabel }: { rows: GroupRow[]; keyLabel: string }) {
  if (!rows.length) return (
    <div style={{ fontSize: 10, color: 'var(--t3)', padding: '12px 0' }}>No data yet.</div>
  );
  const cols = [keyLabel, 'Total', 'Taken', 'Skipped', 'Viewed', 'Notes', 'Outcomes'];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{cols.map(h => (
            <th key={h} style={{ ...TH, textAlign: h === keyLabel ? 'left' : 'right' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key}>
              <td style={TD}>{r.key}</td>
              <td style={{ ...TD, textAlign: 'right', color: 'var(--blue-muted)' }}>{r.total}</td>
              <td style={{ ...TD, textAlign: 'right', color: r.taken   > 0 ? 'var(--green)' : 'var(--t3)' }}>{r.taken}</td>
              <td style={{ ...TD, textAlign: 'right', color: r.skipped > 0 ? 'var(--amber)' : 'var(--t3)' }}>{r.skipped}</td>
              <td style={{ ...TD, textAlign: 'right', color: 'var(--t3)' }}>{r.viewed}</td>
              <td style={{ ...TD, textAlign: 'right', color: r.notes    > 0 ? 'var(--cyan)'   : 'var(--t3)' }}>{r.notes}</td>
              <td style={{ ...TD, textAlign: 'right', color: r.outcomes > 0 ? 'var(--purple)' : 'var(--t3)' }}>{r.outcomes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Journal review table ─────────────────────────────────────────────────────

function LogTable({ entries, onDelete }: {
  entries: Journal71AEntry[];
  onDelete: (id: string) => void;
}) {
  if (!entries.length) return (
    <div style={{ fontSize: 10, color: 'var(--t3)', padding: '16px 0', textAlign: 'center' }}>
      No entries yet. Use the ▼ ATTRIBUTION button on scanner rows to log activity.
    </div>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
        <thead>
          <tr>
            {['Time', 'Symbol', 'Action', 'Setup', 'Dir', 'Score', 'Regime', 'Gamma', 'Outcome', 'Note', ''].map(h => (
              <th key={h} style={{ ...TH, textAlign: h === '' ? 'center' : 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id}>
              <td style={{ ...TD, color: 'var(--blue-dim)', fontSize: 9, whiteSpace: 'nowrap' }}>{fmt(e.ts)}</td>
              <td style={{ ...TD, color: 'var(--gold)', fontWeight: 700 }}>{e.symbol}</td>
              <td style={{ ...TD }}>
                <span style={{
                  fontSize: 8, fontWeight: 700,
                  color: ACTION_COLOR[e.actionType],
                  border: `1px solid ${ACTION_COLOR[e.actionType]}55`,
                  background: `${ACTION_COLOR[e.actionType]}12`,
                  padding: '1px 5px', borderRadius: 3,
                }}>
                  {ACTION_LABEL[e.actionType]}
                </span>
              </td>
              <td style={{ ...TD, color: 'var(--blue-muted)', fontSize: 9, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.setupType?.replace(/_/g,' ') ?? '—'}
              </td>
              <td style={{ ...TD, color: e.direction === 'long' ? 'var(--green)' : e.direction === 'short' ? 'var(--red)' : 'var(--t3)', fontSize: 9 }}>
                {e.direction ?? '—'}
              </td>
              <td style={{ ...TD, textAlign: 'right' }}>{e.score ?? '—'}</td>
              <td style={{ ...TD, color: 'var(--gold)', fontSize: 9, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.regime ?? '—'}
              </td>
              <td style={{ ...TD, fontSize: 8, color: 'var(--blue-dim)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.gammaStatus ?? '—'}
              </td>
              <td style={{ ...TD }}>
                {e.outcome ? (
                  <span style={{
                    fontSize: 8, fontWeight: 700,
                    color: OUTCOME_COLOR[e.outcome],
                    border: `1px solid ${OUTCOME_COLOR[e.outcome]}55`,
                    background: `${OUTCOME_COLOR[e.outcome]}12`,
                    padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase',
                  }}>{e.outcome}</span>
                ) : '—'}
              </td>
              <td style={{ ...TD, color: 'var(--t2)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.note || '—'}
              </td>
              <td style={{ ...TD, textAlign: 'center' }}>
                <button
                  onClick={() => onDelete(e.id)}
                  title="Delete entry"
                  style={{
                    fontSize: 9, color: 'var(--red)', background: 'none',
                    border: '1px solid var(--red)33', borderRadius: 3,
                    padding: '1px 5px', cursor: 'pointer',
                  }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

type TopTab    = 'analytics' | 'log' | 'replay' | 'performance' | 'discipline' | 'session' | 'export' | 'review' | 'edge';
type AnalyView = 'summary' | 'symbol' | 'setup' | 'regime' | 'action' | 'gamma';

export function Analytics71APanel() {
  const entries                = useStore(s => s.journal71aEntries);
  const activeSymbol           = useStore(s => s.activeSymbol);
  const deleteJournal71AEntry  = useStore(s => s.deleteJournal71AEntry);
  const clearJournal71AEntries = useStore(s => s.clearJournal71AEntries);

  const [topTab,   setTopTab]   = useState<TopTab>('analytics');
  const [analView, setAnalView] = useState<AnalyView>('summary');
  const [logFilter, setLogFilter] = useState('');

  const total    = entries.length;
  const taken    = useMemo(() => entries.filter(e => e.actionType === 'trade_taken').length,      [entries]);
  const skipped  = useMemo(() => entries.filter(e => e.actionType === 'trade_skipped').length,    [entries]);
  const viewed   = useMemo(() => entries.filter(e => e.actionType === 'setup_viewed').length,     [entries]);
  const notes    = useMemo(() => entries.filter(e => e.actionType === 'note_added').length,       [entries]);
  const outcomes = useMemo(() => entries.filter(e => e.actionType === 'outcome_reviewed').length, [entries]);

  const wins      = useMemo(() => entries.filter(e => e.outcome === 'win').length,       [entries]);
  const losses    = useMemo(() => entries.filter(e => e.outcome === 'loss').length,      [entries]);
  const breakeven = useMemo(() => entries.filter(e => e.outcome === 'breakeven').length, [entries]);

  const bySymbol = useMemo(() => groupBy(entries, e => e.symbol),      [entries]);
  const bySetup  = useMemo(() => groupBy(entries, e => e.setupType),   [entries]);
  const byRegime = useMemo(() => groupBy(entries, e => e.regime),      [entries]);
  const byAction = useMemo(() => groupBy(entries, e => e.actionType),  [entries]);
  const byGamma  = useMemo(() => groupBy(entries, e => e.gammaStatus), [entries]);

  const filteredLog = useMemo(() => {
    if (!logFilter.trim()) return entries;
    const f = logFilter.toLowerCase();
    return entries.filter(e =>
      e.symbol.toLowerCase().includes(f) ||
      (e.setupType ?? '').toLowerCase().includes(f) ||
      (e.regime ?? '').toLowerCase().includes(f) ||
      e.actionType.includes(f) ||
      e.note.toLowerCase().includes(f)
    );
  }, [entries, logFilter]);

  const tabBtn = (id: TopTab): React.CSSProperties => ({
    fontSize: 10, fontFamily: 'var(--mono)', fontWeight: topTab === id ? 700 : 400,
    color: topTab === id ? 'var(--gold)' : 'var(--t3)',
    background: topTab === id ? 'var(--gold)12' : 'none',
    border: `1px solid ${topTab === id ? 'var(--gold)55' : 'var(--border)'}`,
    padding: '3px 12px', borderRadius: 3, cursor: 'pointer',
  });

  const subBtn = (id: AnalyView): React.CSSProperties => ({
    fontSize: 9, fontFamily: 'var(--mono)', fontWeight: analView === id ? 700 : 400,
    color: analView === id ? 'var(--gold)' : 'var(--t3)',
    background: analView === id ? 'var(--gold)12' : 'none',
    border: `1px solid ${analView === id ? 'var(--gold)55' : 'var(--border)'}`,
    padding: '2px 8px', borderRadius: 3, cursor: 'pointer', letterSpacing: '.3px',
  });

  if (!total) {
    return (
      <div style={{ padding: '28px 0', color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 8 }}>
          📊 Activity Log
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.6 }}>
          No activity logged yet.<br />
          Expand a scanner row → ▼ ATTRIBUTION → use the log buttons.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>

      {/* Top tab row */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <button style={tabBtn('analytics')} onClick={() => setTopTab('analytics')}>Analytics</button>
        <button style={tabBtn('log')}       onClick={() => setTopTab('log')}>
          Log {total > 0 ? `(${total})` : ''}
        </button>
        <button style={tabBtn('replay')}      onClick={() => setTopTab('replay')}>⏮ Replay</button>
        <button style={tabBtn('performance')} onClick={() => setTopTab('performance')}>📈 Performance</button>
        <button style={tabBtn('discipline')}  onClick={() => setTopTab('discipline')}>🎯 Discipline</button>
        <button style={tabBtn('session')}     onClick={() => setTopTab('session')}>📋 Session</button>
        <button style={tabBtn('export')}      onClick={() => setTopTab('export')}>📦 Export</button>
        <button style={tabBtn('review')}     onClick={() => setTopTab('review')}>📚 Review</button>
        <button style={tabBtn('edge')}       onClick={() => setTopTab('edge')}>🔬 Edge</button>
        <button
          onClick={() => { if (window.confirm('Clear all activity log entries?')) clearJournal71AEntries(); }}
          style={{ marginLeft: 'auto', fontSize: 8, color: 'var(--red)', background: 'none',
            border: '1px solid var(--red)44', padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>
          Clear All
        </button>
        <button
          onClick={() => exportJournalCSV(entries)}
          title="Export activity log as CSV"
          style={{ fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
            color: 'var(--blue-dim)', border: '1px solid var(--border)',
            background: 'none', padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>
          ⬇ CSV
        </button>
        <button
          onClick={() => exportJournalJSON(entries)}
          title="Export activity log as JSON"
          style={{ fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
            color: 'var(--blue-dim)', border: '1px solid var(--border)',
            background: 'none', padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>
          ⬇ JSON
        </button>
      </div>

      {/* ─── ANALYTICS TAB ─────────────────────────────────────── */}
      {topTab === 'analytics' && (
        <>
          {/* KPI grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, marginBottom: 12 }}>
            <KPI label="Total"    value={total}    color="var(--blue-muted)" />
            <KPI label="Taken"    value={taken}    color="var(--green)"
              sub={total ? `${(taken/total*100).toFixed(0)}%` : undefined} />
            <KPI label="Skipped"  value={skipped}  color="var(--amber)"
              sub={total ? `${(skipped/total*100).toFixed(0)}%` : undefined} />
            <KPI label="Notes"    value={notes}    color="var(--cyan)"  />
            <KPI label="Outcomes" value={outcomes} color="var(--purple)" />
            <KPI label="Viewed"   value={viewed}   color="var(--t2)"    />
          </div>

          {/* Outcome summary (when outcomes exist) */}
          {outcomes > 0 && (
            <div style={{
              display: 'flex', gap: 10, marginBottom: 12,
              padding: '7px 10px', background: 'var(--surface2)',
              border: '1px solid var(--border)', borderRadius: 4, fontSize: 10,
            }}>
              <span style={{ color: 'var(--t3)', marginRight: 4 }}>Outcomes reviewed:</span>
              {wins > 0      && <span style={{ color: 'var(--green)',  fontFamily: 'var(--mono)', fontWeight: 700 }}>{wins} WIN</span>}
              {losses > 0    && <span style={{ color: 'var(--red)',    fontFamily: 'var(--mono)', fontWeight: 700 }}>{losses} LOSS</span>}
              {breakeven > 0 && <span style={{ color: 'var(--amber)',  fontFamily: 'var(--mono)', fontWeight: 700 }}>{breakeven} BE</span>}
              {(taken + skipped) > 0 &&
                <span style={{ color: 'var(--t3)', marginLeft: 8 }}>
                  Selection rate: {(taken / (taken + skipped) * 100).toFixed(0)}% of decided setups taken
                </span>
              }
            </div>
          )}

          {/* Sub-view tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {(['summary','symbol','setup','regime','action','gamma'] as AnalyView[]).map(v => (
              <button key={v} style={subBtn(v)} onClick={() => setAnalView(v)}>
                {v === 'summary' ? 'Summary'
                  : v === 'symbol' ? 'Symbol'
                  : v === 'setup'  ? 'Setup'
                  : v === 'regime' ? 'Regime'
                  : v === 'action' ? 'Action'
                  : 'Gamma'}
              </button>
            ))}
          </div>

          {analView === 'summary' && (
            <>
              <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 8, fontStyle: 'italic' }}>
                Top symbols by total activity:
              </div>
              <GroupTable rows={bySymbol.slice(0, 6)} keyLabel="Symbol" />
            </>
          )}
          {analView === 'symbol'  && <GroupTable rows={bySymbol}  keyLabel="Symbol"      />}
          {analView === 'setup'   && <GroupTable rows={bySetup}   keyLabel="Setup Type"  />}
          {analView === 'regime'  && (
            <>
              {byRegime.some(r => r.key !== '—') ? (
                <GroupTable rows={byRegime} keyLabel="Regime" />
              ) : (
                <div style={{ fontSize: 10, color: 'var(--t3)', padding: '12px 0' }}>
                  Regime data will populate here once entries are logged while market context is active.
                </div>
              )}
            </>
          )}
          {analView === 'action'  && <GroupTable rows={byAction}  keyLabel="Action Type" />}
          {analView === 'gamma'   && (
            <>
              <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 8 }}>
                Gamma status recorded at log time. Reflects GAMMA_INFLUENCE_FLAGS — not a scoring input.
              </div>
              <GroupTable rows={byGamma} keyLabel="Gamma Status" />
            </>
          )}
        </>
      )}

      {/* ─── LOG TAB ──────────────────────────────────────────────── */}
      {topTab === 'log' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <input
              placeholder="Filter by symbol, setup, regime, note…"
              value={logFilter}
              onChange={e => setLogFilter(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 3, color: 'var(--text)', fontFamily: 'var(--sans)',
                fontSize: 11, padding: '5px 8px', outline: 'none',
              }}
            />
          </div>
          {logFilter && (
            <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 6 }}>
              {filteredLog.length} of {total} entries
            </div>
          )}
          <LogTable entries={filteredLog} onDelete={deleteJournal71AEntry} />
        </>
      )}

      {/* Phase 7.1B tabs */}
      {topTab === 'replay'      && <ReplayPanel />}
      {topTab === 'performance' && <OperatorPerformancePanel />}
      {topTab === 'discipline'  && <DisciplineScorePanel />}

      {/* Phase 7.1C tabs */}
      {topTab === 'session' && <SessionReviewPanel />}
      {topTab === 'export'  && <AuditExportPanel />}

      {/* Phase 7.1D tabs */}
      {topTab === 'review' && <SnapshotHistoryPanel symbol={activeSymbol ?? undefined} />}
      {topTab === 'edge'   && <AdaptiveEdgeSummaryView />}
    </div>
  );
}
